import { DatabaseUtils } from './supabaseClient';
import { SecurityUtils } from './security-utils';
import { analytics } from './analytics';
import { SimpleMarkdownParser } from './utils/markdown-parser';

const uiStrings: Record<string, Record<string, string>> = {
    en: {
        skipLink: 'Skip to main content',
        'nav.partners': 'Partner Exchanges',
        'nav.about': 'About',
        'nav.aboutSubtitle': 'Why CoinPass',
        'nav.howTo': 'How It Works',
        'nav.howToSubtitle': '3-step guide',
        'nav.guides': 'Guides',
        'nav.guidesSubtitle': 'Tips & updates',
        'nav.faq': 'FAQ',
        'hero.cta': 'View Discounts',
        'partners.title': 'Partner Exchanges',
        'howTo.title': 'Save on fees in 3 simple steps',
        'howTo.step1.title': 'Sign Up',
        'howTo.step1.desc': 'Create an account with our partner exchange using the CoinPass link.',
        'howTo.step2.title': 'Trade',
        'howTo.step2.desc': 'Complete verification and trade as usual on the exchange.',
        'howTo.step3.title': 'Get Discounts',
        'howTo.step3.desc': 'Fee discounts are applied automatically to eligible trades.',
        'faq.title': 'Frequently Asked Questions',
        'support.title': 'Support',
        'support.desc': 'Questions or issues? Reach out to us on Telegram and we will get back to you quickly.',
        'support.cta': 'Contact on Telegram',
        'footer.disclaimer': 'This service provides information only and does not constitute investment advice. All investment decisions are your responsibility.',
        'popup.close24h': 'Hide for 24 hours',
        'popup.close': 'Close',
        'card.cta': 'Sign up and save',
    },
};

interface SiteData {
    hero?: {
        title: { ko: string };
        subtitle: { ko: string };
    } | null;
    aboutUs?: {
        title: string;
        content: string;
    };
    exchanges?: any[];
    faqs?: any[];
    popup?: {
        enabled: boolean;
        type: 'text' | 'image';
        content: string;
        imageUrl: string;
        startDate: string;
        endDate: string;
    };
    support?: {
        telegramUrl: string;
    };
}

let siteData: SiteData = {};
const currentLang = 'en';

document.addEventListener('DOMContentLoaded', async () => {
    setupLanguage();
    await loadRemoteContent();
    renderContent();
    setupEventListeners();
});

async function loadRemoteContent() {
    try {
        // Use DatabaseUtils instead of API client - hero ?곗씠??異붽?
        const [exchangesData, faqsData, heroData, aboutData] = await Promise.all([
            DatabaseUtils.getExchanges(),
            DatabaseUtils.getFAQs(),
            DatabaseUtils.getPageContent('hero'),  // hero ?곗씠??濡쒕뱶 異붽?
            DatabaseUtils.getPageContent('about')
        ]);
        
        // display_order濡??뺣젹 (?レ옄媛 ?묒쓣?섎줉 ?욎뿉 ?쒖떆)
        let exchanges = exchangesData;
        if (exchanges && Array.isArray(exchanges)) {
            exchanges = exchanges.sort((a: any, b: any) => {
                const orderA = a.display_order ?? 999999;  // display_order媛 ?놁쑝硫?留??ㅻ줈
                const orderB = b.display_order ?? 999999;
                if (orderA === orderB) {
                    return (a.id || 0) - (b.id || 0);  // 媛숈? ?쒖꽌硫?ID濡??뺣젹
                }
                return orderA - orderB;
            });
        }
        
        const singlePages = aboutData ? [aboutData] : [];
    
    // Process hero data the same way as index.tsx
    console.log('Raw heroData from database:', heroData);
    
    let processedHeroData = null;
    
    if (heroData && heroData.content) {
        console.log('heroData.content type:', typeof heroData.content);
        console.log('heroData.content value:', heroData.content);
        
        const content = typeof heroData.content === 'string' ? JSON.parse(heroData.content) : heroData.content;
        console.log('Parsed content:', content);
        
        processedHeroData = {
            title: { ko: content?.title || '' },
            subtitle: { ko: content?.subtitle || '' }
        };
        console.log('Processed hero data:', processedHeroData);
    } else {
        console.log('No hero data from database');
    }
    
    siteData = {
        exchanges: exchanges || [],
        faqs: faqsData || [],
        hero: processedHeroData,  // null?댁뼱??hero ?꾨뱶 ?ㅼ젙
    };
    
    console.log('Final siteData.hero:', siteData.hero);
    
    // Debug logging for aboutUs
    console.log('Loading page contents:', singlePages?.length, 'pages found');
    
    if (aboutData && aboutData.content) {
        siteData.aboutUs = aboutData.content;
        console.log('About/AboutUs data loaded:', aboutData.content);
    }
    
    // Check if aboutUs was loaded
    if (!siteData.aboutUs) {
        console.warn('AboutUs data not found in page_contents (looking for page_type="about")');
    }
    } catch (error) {
        console.error('Failed to load site data:', error);
        // Track error
        analytics.trackError('Failed to load site data', error?.toString());
    }
}

function renderContent() {
    if (!siteData) return;
    setupHero(siteData.hero);
    updateAboutUs(siteData.aboutUs);
    const allExchanges = siteData.exchanges || [];
    populateExchangeGrid('exchange-grid', allExchanges);
    updateFaqs(siteData.faqs || []);
    updateSupportSection(siteData.support);
    setupPopup();
    setupScrollAnimations();
}

function setupEventListeners() {
    setupMobileMenu();
    setupNavigation();
}

function setupLanguage() {
    document.documentElement.lang = 'en';
    translateUI();
}


function translateUI() {
    document.querySelectorAll('[data-lang-key]').forEach(el => {
        const key = el.getAttribute('data-lang-key');
        if (key && uiStrings[currentLang][key]) {
            el.textContent = uiStrings[currentLang][key];
        }
    });
}

class TypingAnimator {
    private el!: HTMLElement;
    private phrases!: string[];
    private loopNum: number = 0;
    private typingSpeed: number = 100;
    private erasingSpeed: number = 50;
    private delayBetweenPhrases: number = 2000;
    private isPaused: boolean = false;
    private timeoutId: number | null = null;

    constructor(el: HTMLElement, phrases: string[]) {
        if (!el || !phrases || phrases.length === 0) return;
        this.el = el;
        this.phrases = phrases;
        this.tick();
    }

    public setPhrases(phrases: string[]) {
        this.phrases = phrases;
        this.loopNum = 0;
        if(this.timeoutId) clearTimeout(this.timeoutId);
        this.el.textContent = '';
        if(!this.isPaused) this.tick();
    }

    private tick = async () => {
        if (this.isPaused || !this.el.isConnected) return;
        const i = this.loopNum % this.phrases.length;
        const fullTxt = this.phrases[i];
        for (let j = 0; j < fullTxt.length; j++) {
            if (this.isPaused || !this.el.isConnected) return;
            this.el.textContent = fullTxt.substring(0, j + 1);
            await this.sleep(this.typingSpeed);
        }
        await this.sleep(this.delayBetweenPhrases);
        for (let j = fullTxt.length; j > 0; j--) {
            if (this.isPaused || !this.el.isConnected) return;
            this.el.textContent = fullTxt.substring(0, j - 1);
            await this.sleep(this.erasingSpeed);
        }
        await this.sleep(500);
        this.loopNum++;
        this.timeoutId = window.setTimeout(this.tick, 0);
    }

    private sleep(ms: number): Promise<void> {
        return new Promise(resolve => { this.timeoutId = window.setTimeout(resolve, ms); });
    }
    public pause() { this.isPaused = true; if(this.timeoutId) clearTimeout(this.timeoutId); }
    public resume() { if(this.isPaused) { this.isPaused = false; this.tick(); } }
}

let heroAnimator: TypingAnimator | undefined;
function setupHero(heroData: any) {
    console.log('setupHero called with:', heroData);
    
    const titleEl = document.getElementById('hero-title');
    const subtitleEl = document.getElementById('hero-subtitle');
    const heroSection = document.querySelector('.hero');
    
    console.log('Hero elements found:', {
        titleEl: !!titleEl,
        subtitleEl: !!subtitleEl,
        heroSection: !!heroSection
    });
    
    // 湲곕낯媛??ㅼ젙
    const defaultPhrases = [
        'Save up to 50% on trading fees.',
        'Verified discounts from official partners.',
        'Join once and keep saving on every trade.'
    ];
    
    // hero ?곗씠?곌? ?놁뼱??湲곕낯媛믪쑝濡??쒖떆
    if (!heroData) {
        if (titleEl && !heroAnimator) {
            heroAnimator = new TypingAnimator(titleEl as HTMLElement, defaultPhrases);
            if(heroSection){
                const observer = new IntersectionObserver(entries => {
                    entries.forEach(entry => entry.isIntersecting ? heroAnimator?.resume() : heroAnimator?.pause());
                });
                observer.observe(heroSection);
            }
        }
        if (subtitleEl) {
        subtitleEl.textContent = 'Verified partner discounts across top global exchanges.';
        }
        return;
    }

    // hero ?곗씠?곌? ?덉쓣 ??- ?대? processed??援ъ“?붾맂 ?곗씠???ъ슜
    if (titleEl) {
        const title = heroData.title?.ko || '';
        const sanitizedTitle = SecurityUtils.sanitizeHtml(title);
        const phrases = sanitizedTitle ? sanitizedTitle.split('\n').filter((p: string) => p.trim() !== '') : defaultPhrases;
        
        if (!heroAnimator) {
             heroAnimator = new TypingAnimator(titleEl as HTMLElement, phrases);
             if(heroSection){
                const observer = new IntersectionObserver(entries => {
                    entries.forEach(entry => entry.isIntersecting ? heroAnimator?.resume() : heroAnimator?.pause());
                });
                observer.observe(heroSection);
            }
        } else {
            heroAnimator.setPhrases(phrases);
        }
    }
    
    if (subtitleEl) {
        const subtitle = heroData.subtitle?.ko || 'Verified partner discounts across top global exchanges.';
        subtitleEl.textContent = SecurityUtils.sanitizeHtml(subtitle);
    }
}

function updateAboutUs(aboutUsData: any) {
    const titleEl = document.getElementById('about-us-title');
    const contentEl = document.getElementById('about-us-content');
    
    // Debug logging
    console.log('UpdateAboutUs called with:', aboutUsData);
    
    if (!aboutUsData) {
        console.warn('No aboutUs data provided to updateAboutUs function');
        
        // Set default content if no data
        if (titleEl) {
            titleEl.textContent = 'About CoinPass';
        }
        if (contentEl) {
            contentEl.innerHTML = '<p>CoinPass provides verified fee discounts for major exchanges through official partner links.</p>';
        }
        return;
    }

    if (titleEl && aboutUsData.title) {
        titleEl.textContent = SecurityUtils.sanitizeHtml(aboutUsData.title || '');
        console.log('AboutUs title updated:', aboutUsData.title);
    }
    if (contentEl && aboutUsData.content) {
        // 留덊겕?ㅼ슫 ?뚯떛 ?곸슜 (Bold? 留곹겕 吏??
        const parsedContent = SimpleMarkdownParser.parse(aboutUsData.content || '');
        contentEl.innerHTML = parsedContent;
        console.log('AboutUs content updated with markdown parsing');
    }
}

function populateExchangeGrid(gridId: string, exchangesData: any[]) {
    const gridEl = document.getElementById(gridId);
    if (!gridEl || !exchangesData) return;
    
    const fragment = document.createDocumentFragment();
    exchangesData.forEach(exchange => {
        const card = document.createElement('div');
        card.className = 'exchange-card anim-fade-in';
        
        // XSS 諛⑹?瑜??꾪븳 ?곗씠??sanitization
        const name = SecurityUtils.sanitizeHtml(exchange.name_ko || '');
        const benefit1Tag = SecurityUtils.sanitizeHtml(exchange.benefit1_tag_ko || '');
        const benefit1Value = SecurityUtils.sanitizeHtml(exchange.benefit1_value_ko || '');
        const benefit2Tag = SecurityUtils.sanitizeHtml(exchange.benefit2_tag_ko || '');
        const benefit2Value = SecurityUtils.sanitizeHtml(exchange.benefit2_value_ko || '');
        const benefit3Tag = SecurityUtils.sanitizeHtml(exchange.benefit3_tag_ko || '');
        const benefit3Value = SecurityUtils.sanitizeHtml(exchange.benefit3_value_ko || '');
        const benefit4Tag = SecurityUtils.sanitizeHtml(exchange.benefit4_tag_ko || '');
        const benefit4Value = SecurityUtils.sanitizeHtml(exchange.benefit4_value_ko || '');

        let logoHtml = '';
        if (exchange.logoimageurl && SecurityUtils.isValidUrl(exchange.logoimageurl)) {
            const sanitizedUrl = SecurityUtils.sanitizeHtml(exchange.logoimageurl);
            logoHtml = `<div class="exchange-logo"><img src="${sanitizedUrl}" alt="${name} logo" loading="lazy" onerror="this.style.display='none'"></div>`;
        } else {
            logoHtml = `<div class="exchange-logo exchange-logo-text">${name?.substring(0, 3).toUpperCase() || 'N/A'}</div>`;
        }

        // ?쒗깮 ??ぉ?ㅼ쓣 諛곗뿴濡??뺣━
        const benefits = [
            { tag: benefit1Tag, value: benefit1Value },
            { tag: benefit2Tag, value: benefit2Value },
            { tag: benefit3Tag, value: benefit3Value },
            { tag: benefit4Tag, value: benefit4Value }
        ];

        // 鍮?媛믪씠 ?꾨땶 ?쒗깮?ㅻ쭔 ?꾪꽣留?
        const validBenefits = benefits.filter(benefit => benefit.tag && benefit.value);
        
        // ?쒗깮 由ъ뒪??HTML ?앹꽦
        const benefitsHtml = validBenefits.map(benefit => 
            `<li><span class="tag">${benefit.tag}</span> <strong>${benefit.value}</strong></li>`
        ).join('');

        card.innerHTML = `
            <div class="card-header">
                ${logoHtml}
                <h4>${name}</h4>
            </div>
            <ul class="benefits-list">
                ${benefitsHtml}
            </ul>
            <a href="${SecurityUtils.isValidUrl(exchange.link) ? SecurityUtils.sanitizeHtml(exchange.link) : '#'}" class="card-cta" target="_blank" rel="noopener noreferrer nofollow" data-exchange-name="${name}">${uiStrings[currentLang]['card.cta']}</a>
        `;
        
        // Add click tracking for exchange links
        const ctaButton = card.querySelector('.card-cta');
        if (ctaButton) {
            ctaButton.addEventListener('click', (e) => {
                const target = e.target as HTMLElement;
                const exchangeName = target.getAttribute('data-exchange-name') || 'Unknown';
                const link = target.getAttribute('href') || '';
                
                // Track the exchange click
                analytics.trackExchangeClick(exchangeName, link);
                analytics.trackConversion('exchange_referral', undefined);
            });
        }
        
        fragment.appendChild(card);
    });
    gridEl.innerHTML = '';
    gridEl.appendChild(fragment);
}


function updateFaqs(faqsData: any[]) {
    const faqContainerEl = document.getElementById('faq-container');
    if (!faqContainerEl || !faqsData) return;
    
    const fragment = document.createDocumentFragment();
    faqsData.forEach(faq => {
        const details = document.createElement('details');
        details.className = 'anim-fade-in';
        const summary = document.createElement('summary');
        // FAQ 吏덈Ц? 留덊겕?ㅼ슫 ?뚯떛 ?곸슜
        summary.innerHTML = SimpleMarkdownParser.parse(faq.question_ko || '');
        const contentDiv = document.createElement('div');
        contentDiv.className = 'faq-content';
        // FAQ ?듬???留덊겕?ㅼ슫 ?뚯떛 ?곸슜 (Bold? 留곹겕 吏??
        contentDiv.innerHTML = SimpleMarkdownParser.parse(faq.answer_ko || '');
        details.appendChild(summary);
        details.appendChild(contentDiv);
        fragment.appendChild(details);
    });
    faqContainerEl.innerHTML = '';
    faqContainerEl.appendChild(fragment);
}

function updateSupportSection(supportData: any) {
    const linkEl = document.getElementById('telegram-support-link');
    if (linkEl && supportData?.telegramUrl) {
        linkEl.setAttribute('href', supportData.telegramUrl);
    }
}

function setupScrollAnimations() {
    const observer = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('is-visible');
                observer.unobserve(entry.target);
            }
        });
    }, { threshold: 0.1 });
    document.querySelectorAll('.anim-fade-in').forEach(el => observer.observe(el));
}

function setupMobileMenu() {
    const hamburgerButton = document.querySelector('.hamburger-button') as HTMLButtonElement;
    const mainNav = document.getElementById('main-nav');
    
    if (!hamburgerButton || !mainNav) {
        console.warn('Hamburger menu elements not found');
        return;
    }
    
    // Toggle menu on hamburger click
    hamburgerButton.addEventListener('click', function() {
        const isActive = hamburgerButton.classList.contains('is-active');
        
        if (isActive) {
            // Close menu
            hamburgerButton.classList.remove('is-active');
            hamburgerButton.setAttribute('aria-expanded', 'false');
            mainNav.classList.remove('active');
            document.body.style.overflow = '';
        } else {
            // Open menu
            hamburgerButton.classList.add('is-active');
            hamburgerButton.setAttribute('aria-expanded', 'true');
            mainNav.classList.add('active');
            document.body.style.overflow = 'hidden';
        }
    });
    
    // Close menu when clicking outside
    document.addEventListener('click', function(event) {
        const target = event.target as HTMLElement;
        if (!hamburgerButton.contains(target) && !mainNav.contains(target)) {
            if (hamburgerButton.classList.contains('is-active')) {
                hamburgerButton.classList.remove('is-active');
                hamburgerButton.setAttribute('aria-expanded', 'false');
                mainNav.classList.remove('active');
                document.body.style.overflow = '';
            }
        }
    });
    
    // Close menu on escape key
    document.addEventListener('keydown', function(event) {
        if (event.key === 'Escape' && hamburgerButton.classList.contains('is-active')) {
            hamburgerButton.classList.remove('is-active');
            hamburgerButton.setAttribute('aria-expanded', 'false');
            mainNav.classList.remove('active');
            document.body.style.overflow = '';
        }
    });
    
    // Handle window resize
    window.addEventListener('resize', function() {
        if (window.innerWidth > 768 && hamburgerButton.classList.contains('is-active')) {
            hamburgerButton.classList.remove('is-active');
            hamburgerButton.setAttribute('aria-expanded', 'false');
            mainNav.classList.remove('active');
            document.body.style.overflow = '';
        }
    });
    
    // Close menu when clicking nav links
    mainNav.querySelectorAll('a').forEach(link => {
        link.addEventListener('click', () => {
            hamburgerButton.classList.remove('is-active');
            mainNav.classList.remove('active');
            hamburgerButton.setAttribute('aria-expanded', 'false');
            document.body.style.overflow = '';
        });
    });
}

function setupNavigation() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function (this: HTMLAnchorElement, e) {
            const href = this.getAttribute('href');
            if(href && href !== '#') {
                e.preventDefault();
                const targetElement = document.querySelector(href);
                if (targetElement) {
                    targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }
        });
    });
}

function setupPopup() {
    if (!siteData.popup || !siteData.popup.enabled) return;
    const hideUntil = localStorage.getItem('coinpass-popup-hide-until');
    if (hideUntil && Date.now() < parseInt(hideUntil, 10)) return;

    const now = new Date();
    const startDate = siteData.popup.startDate ? new Date(siteData.popup.startDate) : null;
    const endDate = siteData.popup.endDate ? new Date(siteData.popup.endDate) : null;
    if ((startDate && now < startDate) || (endDate && now > endDate)) return;

    const container = document.getElementById('popup-container');
    const imageEl = document.getElementById('popup-image');
    const textEl = document.getElementById('popup-text');
    const closeBtn = document.getElementById('popup-close');
    const close24hBtn = document.getElementById('popup-close-24h');
    const overlay = container?.querySelector('.popup-overlay');
    if (!container || !imageEl || !textEl || !closeBtn || !close24hBtn) return;
    
    const contentToDisplay = siteData.popup.content || '';

    if (siteData.popup.type === 'image' && siteData.popup.imageUrl) {
        (imageEl as HTMLImageElement).src = siteData.popup.imageUrl;
        (imageEl as HTMLElement).style.display = 'block';
        (textEl as HTMLElement).style.display = 'none';
    } else if (siteData.popup.type === 'text' && contentToDisplay) {
        textEl.textContent = contentToDisplay;
        (textEl as HTMLElement).style.display = 'block';
        (imageEl as HTMLElement).style.display = 'none';
    } else return;
    
    container.style.display = 'flex';
    const closePopup = () => container.style.display = 'none';
    closeBtn.onclick = closePopup;
    if(overlay) overlay.addEventListener('click', closePopup);
    close24hBtn.onclick = () => {
        localStorage.setItem('coinpass-popup-hide-until', (Date.now() + 24 * 60 * 60 * 1000).toString());
        closePopup();
    };
}


